/**
 * Brings an existing catalog up to date with the app without losing what the business
 * wrote in it.
 *
 * Regenerating the catalog from scratch would drop the "Plantilla" columns and the
 * glossary, which are maintained by hand and cannot be derived from the locale files.
 * So the workbook is edited in place: new keys are appended, retired keys are flagged,
 * and rows affected by a terminology change get a DRAFT of the new text pre-filled for
 * review. Nothing a person wrote is ever overwritten.
 */

import type { Cell, Workbook, Worksheet } from "exceljs";
import {
  applyDataRowStyle,
  buildCatalogRow,
  buildGlossaryRows,
  CATALOG_COLUMNS,
  COLUMNS_BY_LOCALE,
  flattenLocales,
  getCatalogGlossaryTemplates,
  getCellText,
  getSheetNameForMainKey,
  GLOSSARY_SHEET_NAME,
  initCatalogSheet,
  INSTRUCTIONS_SHEET_NAME,
  loadExcelJs,
  LOCALES,
  NOTES,
  toSheetValues,
  type LocaleCode,
  type LocalesByCode,
} from "./i18nCatalog";
import {
  collapseNestedGlossaryReferences,
  expandTemplate,
  getChangedEntities,
  GLOSSARY_KEY_PREFIX,
  hasGlossaryReference,
  getReferencedEntities,
  hasMalformedGlossaryReference,
  isWholeTermTemplate,
  learnEntityCasing,
  type EntityCasing,
  resolveReferences,
  type ExpansionStatus,
  type Glossary,
} from "./i18nGlossary";

export const UPDATED_CATALOG_FILE_NAME = "i18n-catalog-actualizado.xlsx";

export interface ManualRow {
  fullKey: string;
  locale: LocaleCode;
  sheetName: string;
  rowNumber: number;
  status: ExpansionStatus;
  reason: string;
}

export interface CatalogUpdateReport {
  newKeys: string[];
  obsoleteKeys: string[];
  refreshedCurrentTexts: number;
  preservedTemplates: number;
  seededTemplates: number;
  /** Nested references unwrapped back into the reference they were meant to be. */
  repairedTemplates: number;
  glossaryEntriesAdded: number;
  glossaryEntriesRefreshed: number;
  changedEntities: string[];
  draftsClean: number;
  draftsNeedingReview: number;
  /** Rows the glossary override alone takes care of, so no per-row draft was written. */
  coveredByGlossary: number;
  /** References resolved inside a text the client wrote themselves. */
  authoredResolvedClean: number;
  authoredResolvedNeedsReview: number;
  manualRows: ManualRow[];
  /** Broken references that are inert today but will misfire the day their term changes. */
  malformedTemplates: ManualRow[];
  glossaryWarnings: string[];
}

export interface CatalogUpdateResult {
  workbook: Workbook;
  report: CatalogUpdateReport;
}

function isDataSheet(sheet: Worksheet): boolean {
  return sheet.name !== INSTRUCTIONS_SHEET_NAME && sheet.name !== GLOSSARY_SHEET_NAME;
}

/** Reads the terminology sheet. Rows without a `glossary.` key are reported, not guessed at. */
function parseGlossarySheet(workbook: Workbook): { glossary: Glossary; warnings: string[] } {
  const sheet = workbook.getWorksheet(GLOSSARY_SHEET_NAME);
  const glossary: Glossary = {};
  const warnings: string[] = [];

  if (!sheet) {
    return { glossary, warnings: [`El archivo no tiene una hoja "${GLOSSARY_SHEET_NAME}"`] };
  }

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const fullKey = getCellText(row.getCell(CATALOG_COLUMNS.fullKey));

    if (!fullKey) return;

    if (!fullKey.startsWith(GLOSSARY_KEY_PREFIX)) {
      warnings.push(
        `Fila ${rowNumber}: "${fullKey}" no es una key de glosario (debe empezar con ` +
          `"${GLOSSARY_KEY_PREFIX}"), se ignora`,
      );

      return;
    }

    const name = fullKey.split(".").pop();

    if (glossary[name]) {
      warnings.push(`Fila ${rowNumber}: la entidad "${name}" está definida más de una vez`);

      return;
    }

    glossary[name] = {
      name,
      fullKey,
      currentEs: getCellText(row.getCell(CATALOG_COLUMNS.currentEs)),
      newEs: getCellText(row.getCell(CATALOG_COLUMNS.newEs)),
      currentEn: getCellText(row.getCell(CATALOG_COLUMNS.currentEn)),
      newEn: getCellText(row.getCell(CATALOG_COLUMNS.newEn)),
    };
  });

  return { glossary, warnings };
}

/** Keeps the workbook glossary aligned with the locale files without touching client values. */
function syncGlossarySheet(
  workbook: Workbook,
  locales: LocalesByCode,
): { added: number; refreshed: number } {
  let sheet = workbook.getWorksheet(GLOSSARY_SHEET_NAME);

  if (!sheet) {
    sheet = workbook.addWorksheet(GLOSSARY_SHEET_NAME);
    initCatalogSheet(sheet);
  }

  const rowsByKey = new Map<string, ReturnType<Worksheet["getRow"]>>();

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const fullKey = getCellText(row.getCell(CATALOG_COLUMNS.fullKey));

    if (fullKey.startsWith(GLOSSARY_KEY_PREFIX)) rowsByKey.set(fullKey, row);
  });

  let added = 0;
  let refreshed = 0;

  buildGlossaryRows(locales).forEach(defaultEntry => {
    const existing = rowsByKey.get(defaultEntry.fullKey);

    if (!existing) {
      const row = sheet.addRow(toSheetValues(defaultEntry));

      applyDataRowStyle(row, false);
      added++;

      return;
    }

    const currentEs = existing.getCell(CATALOG_COLUMNS.currentEs);
    const currentEn = existing.getCell(CATALOG_COLUMNS.currentEn);
    let entryRefreshed = false;

    if (getCellText(currentEs) !== defaultEntry.currentEs) {
      currentEs.value = defaultEntry.currentEs;
      entryRefreshed = true;
    }

    if (getCellText(currentEn) !== defaultEntry.currentEn) {
      currentEn.value = defaultEntry.currentEn;
      entryRefreshed = true;
    }

    if (entryRefreshed) refreshed++;

    const notes = existing.getCell(CATALOG_COLUMNS.notes);

    if (!getCellText(notes)) notes.value = defaultEntry.notes;
  });

  return { added, refreshed };
}

function appendNote(current: string, note: string): string {
  if (!current) return note;
  if (current.includes(note)) return current;

  return `${current} | ${note}`;
}

/** Where a row lives, carried along so a report entry can point back at it. */
interface RowLocation {
  fullKey: string;
  locale: LocaleCode;
  sheetName: string;
  rowNumber: number;
}

interface TemplateOutcome {
  text: string;
  /** A tokenised value rescued from the current column, where it lived and nowhere else. */
  preserved: boolean;
  /** Filled in from the locale files, which express it as i18next nesting. */
  seeded: boolean;
  /** A doubled reference unwrapped back into the one it was meant to be. */
  repaired: boolean;
}

/**
 * Decides what a row's "Plantilla" column should hold, without writing anything.
 *
 * The column is generated, so it is safe to refresh — but only after rescuing a tokenised
 * value that lives there and nowhere else. Repairing runs last so a row that only suffered
 * a double find and replace goes back to being expandable instead of sitting inert,
 * whichever way its template got there.
 */
export function reconcileTemplate(
  templateText: string,
  currentText: string,
  sourceTemplate: string,
): TemplateOutcome {
  let text = templateText;
  let preserved = false;
  let seeded = false;

  if (!text && hasGlossaryReference(currentText)) {
    text = currentText;
    preserved = true;
  } else if (!text && sourceTemplate) {
    text = sourceTemplate;
    seeded = true;
  }

  const collapsed = collapseNestedGlossaryReferences(text);

  return { text: collapsed, preserved, seeded, repaired: collapsed !== text };
}

/** Counters a single row can bump. */
type RowTally =
  | "authoredResolvedClean"
  | "authoredResolvedNeedsReview"
  | "draftsClean"
  | "draftsNeedingReview"
  | "coveredByGlossary";

/** What a row concluded. Nothing here touches a cell — the orchestrator applies it. */
interface RowOutcome {
  /** Written into "Nuevo texto"; the cell is left alone when absent. */
  text?: string;
  note?: string;
  tally?: RowTally;
  /** Set when no safe wording could be produced and a person has to write it. */
  manual?: { status: ExpansionStatus; reason: string };
}

interface RowContext {
  appText: string;
  templateText: string;
  glossary: Glossary;
  locale: LocaleCode;
  /** How each term is cased in this particular sentence, learned from the row's template. */
  casing: EntityCasing | null;
}

/**
 * Completes a text the client wrote themselves, keeping their wording.
 *
 * Resolving the references they left in it is not overwriting their text — it has to
 * happen here or the importer rejects the row forever.
 */
export function resolveAuthoredText(authoredText: string, ctx: RowContext): RowOutcome {
  const resolved = resolveReferences({
    value: authoredText,
    glossary: ctx.glossary,
    locale: ctx.locale,
    casing: ctx.casing ?? {},
  });

  if (!resolved.text) {
    return { note: NOTES.manual, manual: { status: resolved.status, reason: resolved.reason } };
  }

  if (resolved.status === "clean") {
    return { text: resolved.text, note: NOTES.authoredResolved, tally: "authoredResolvedClean" };
  }

  return {
    text: resolved.text,
    note: `${NOTES.authoredResolvedReview} (${resolved.reason})`,
    tally: "authoredResolvedNeedsReview",
  };
}

/** Proposes new wording for a row whose term sits inside a sentence. */
export function draftFromTemplate(ctx: RowContext): RowOutcome {
  const expansion = expandTemplate({
    template: ctx.templateText,
    appText: ctx.appText,
    glossary: ctx.glossary,
    locale: ctx.locale,
  });

  if (expansion.status === "clean") {
    return { text: expansion.text, note: NOTES.draftClean, tally: "draftsClean" };
  }

  if (expansion.status === "needs-review") {
    return { text: expansion.text, note: NOTES.draftNeedsReview, tally: "draftsNeedingReview" };
  }

  return { note: NOTES.manual, manual: { status: expansion.status, reason: expansion.reason } };
}

/** Appends the keys the app gained since the catalog was produced. Returns their paths. */
function appendMissingKeys(
  workbook: Workbook,
  flat: Record<LocaleCode, Record<string, string>>,
  sourceTemplates: Record<LocaleCode, Record<string, string>>,
  seenKeys: Set<string>,
  sheetByMainKey: Map<string, Worksheet>,
  usedSheetNames: Set<string>,
): string[] {
  const appKeys = [...new Set([...Object.keys(flat.es), ...Object.keys(flat.en)])].sort();

  return appKeys
    .filter(fullKey => !seenKeys.has(fullKey))
    .map(fullKey => {
      const mainKey = fullKey.split(".")[0];
      let sheet = sheetByMainKey.get(mainKey);

      if (!sheet) {
        sheet = workbook.addWorksheet(getSheetNameForMainKey(mainKey, usedSheetNames));
        initCatalogSheet(sheet);
        sheetByMainKey.set(mainKey, sheet);
      }

      const catalogRow = buildCatalogRow(fullKey, flat, sourceTemplates, NOTES.newKey);

      applyDataRowStyle(sheet.addRow(toSheetValues(catalogRow)), catalogRow.hasVars);

      return fullKey;
    });
}

export async function updateCatalogBuffer(
  buffer: ArrayBuffer,
  locales: LocalesByCode,
): Promise<CatalogUpdateResult> {
  const ExcelJS = await loadExcelJs();
  const workbook: Workbook = new ExcelJS.Workbook();

  await workbook.xlsx.load(buffer);

  const flat = flattenLocales(locales);
  const sourceTemplates = getCatalogGlossaryTemplates(locales);
  const glossarySync = syncGlossarySheet(workbook, locales);
  const { glossary, warnings: glossaryWarnings } = parseGlossarySheet(workbook);
  const changedByLocale: Record<LocaleCode, Set<string>> = {
    es: new Set(getChangedEntities(glossary, "es")),
    en: new Set(getChangedEntities(glossary, "en")),
  };

  const report: CatalogUpdateReport = {
    newKeys: [],
    obsoleteKeys: [],
    refreshedCurrentTexts: 0,
    preservedTemplates: 0,
    seededTemplates: 0,
    repairedTemplates: 0,
    glossaryEntriesAdded: glossarySync.added,
    glossaryEntriesRefreshed: glossarySync.refreshed,
    changedEntities: [...new Set([...changedByLocale.es, ...changedByLocale.en])].sort(),
    draftsClean: 0,
    draftsNeedingReview: 0,
    coveredByGlossary: 0,
    authoredResolvedClean: 0,
    authoredResolvedNeedsReview: 0,
    manualRows: [],
    malformedTemplates: [],
    glossaryWarnings,
  };

  const seenKeys = new Set<string>();
  const sheetByMainKey = new Map<string, Worksheet>();
  const usedSheetNames = new Set<string>(workbook.worksheets.map(sheet => sheet.name));

  /** The single place a row outcome reaches the workbook and the report. */
  const applyOutcome = (
    outcome: RowOutcome,
    newCell: Cell,
    notesCell: Cell,
    where: RowLocation,
  ): void => {
    if (outcome.text !== undefined) newCell.value = outcome.text;
    if (outcome.note) notesCell.value = appendNote(getCellText(notesCell), outcome.note);
    if (outcome.tally) report[outcome.tally]++;
    if (outcome.manual) report.manualRows.push({ ...where, ...outcome.manual });
  };

  workbook.eachSheet(sheet => {
    if (!isDataSheet(sheet)) return;

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const fullKey = getCellText(row.getCell(CATALOG_COLUMNS.fullKey));

      if (!fullKey) return;

      seenKeys.add(fullKey);
      sheetByMainKey.set(fullKey.split(".")[0], sheet);

      const existsInApp = flat.es[fullKey] !== undefined || flat.en[fullKey] !== undefined;
      const notesCell = row.getCell(CATALOG_COLUMNS.notes);

      if (!existsInApp) {
        report.obsoleteKeys.push(fullKey);
        notesCell.value = appendNote(getCellText(notesCell), NOTES.obsoleteKey);

        return;
      }

      LOCALES.forEach(locale => {
        const appText = flat[locale][fullKey];

        if (appText === undefined) return;

        const columns = COLUMNS_BY_LOCALE[locale];
        const currentCell = row.getCell(columns.current);
        const templateCell = row.getCell(columns.template);
        const currentText = getCellText(currentCell);
        const template = reconcileTemplate(
          getCellText(templateCell),
          currentText,
          sourceTemplates[locale][fullKey] ?? "",
        );
        const where: RowLocation = { fullKey, locale, sheetName: sheet.name, rowNumber };

        if (template.text !== getCellText(templateCell)) templateCell.value = template.text;
        if (template.preserved) report.preservedTemplates++;
        if (template.seeded) report.seededTemplates++;
        if (template.repaired) {
          report.repairedTemplates++;
          notesCell.value = appendNote(getCellText(notesCell), NOTES.repairedTemplate);
        }

        if (currentText !== appText) {
          currentCell.value = appText;
          report.refreshedCurrentTexts++;
        }

        const templateText = template.text;
        const newCell = row.getCell(columns.newText);
        const authoredText = getCellText(newCell);
        const ctx: RowContext = {
          appText,
          templateText,
          glossary,
          locale,
          casing: templateText
            ? learnEntityCasing({ template: templateText, appText, glossary, locale })
            : null,
        };

        if (hasGlossaryReference(authoredText)) {
          applyOutcome(resolveAuthoredText(authoredText, ctx), newCell, notesCell, where);

          return;
        }

        if (!templateText) return;

        // Reported even when no term changes: a nested reference is silently inert until
        // someone renames that entity, and then it expands into garbage.
        if (hasMalformedGlossaryReference(templateText)) {
          report.malformedTemplates.push({
            ...where,
            status: "malformed",
            reason: "La plantilla tiene referencias anidadas o sin cerrar",
          });
          applyOutcome({ note: NOTES.malformedTemplate }, newCell, notesCell, where);

          return;
        }

        const usesChangedEntity = getReferencedEntities(templateText).some(name =>
          changedByLocale[locale].has(name),
        );

        if (!usesChangedEntity) return;

        // The whole text is the term, which the locale file states through i18next nesting.
        // Overriding the term in the Glosario sheet already reaches this row, so drafting a
        // literal here would do nothing but freeze the term out of that mechanism.
        if (isWholeTermTemplate(templateText)) {
          applyOutcome(
            { note: NOTES.coveredByGlossary, tally: "coveredByGlossary" },
            newCell,
            notesCell,
            where,
          );

          return;
        }

        // Never overwrite a final text a person already wrote.
        if (authoredText) return;

        applyOutcome(draftFromTemplate(ctx), newCell, notesCell, where);
      });
    });
  });

  report.newKeys = appendMissingKeys(
    workbook,
    flat,
    sourceTemplates,
    seenKeys,
    sheetByMainKey,
    usedSheetNames,
  );

  return { workbook, report };
}

export async function updateCatalogFile(
  file: File,
  locales: LocalesByCode,
): Promise<CatalogUpdateResult> {
  return updateCatalogBuffer(await file.arrayBuffer(), locales);
}
